import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const PHOTOS = [
  {
    src: "/assets/fachada.png",
    alt: "Fachada da Ótica Danko em Mogi Guaçu, com letreiro verde",
  },
  {
    src: "/assets/dentro-1.png",
    alt: "Interior da Ótica Danko: mesas de atendimento e mostruários de armações",
  },
  {
    src: "/assets/dentro-2.png",
    alt: "Interior da Ótica Danko: entrada com banner de exame de vista",
  },
  {
    src: "/assets/dentro-3.png",
    alt: "Interior da Ótica Danko: mesa de prova com espelho e poltronas",
  },
];

export const Gallery = () => {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (dir) =>
      setOpen((current) =>
        current === null
          ? current
          : (current + dir + PHOTOS.length) % PHOTOS.length
      ),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (event) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step]);

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      className="scroll-mt-24 bg-stone-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl" data-reveal>
          <p
            data-testid="gallery-overline"
            className="text-xs font-bold uppercase tracking-[0.2em] text-brand"
          >
            A Loja
          </p>
          <h2
            data-testid="gallery-heading"
            className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900"
          >
            Um espaço pensado para atender você
          </h2>
          <p
            data-testid="gallery-text"
            className="mt-5 text-base sm:text-lg leading-relaxed text-stone-600"
          >
            Conheça por dentro a nossa loja em Mogi Guaçu: ambiente amplo,
            confortável e preparado para você escolher com calma.
          </p>
        </div>

        <div className="mt-14 space-y-6">
          <button
            type="button"
            onClick={() => setOpen(0)}
            data-testid="gallery-photo-1"
            data-reveal
            aria-label="Ampliar foto 1"
            className="group block w-full overflow-hidden rounded-2xl border border-stone-200 shadow-sm hover:shadow-lg transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <img
              src={PHOTOS[0].src}
              alt={PHOTOS[0].alt}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </button>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PHOTOS.slice(1).map((photo, index) => (
              <button
                key={photo.src}
                type="button"
                onClick={() => setOpen(index + 1)}
                data-testid={`gallery-photo-${index + 2}`}
                data-reveal
                style={{ transitionDelay: `${index * 100}ms` }}
                aria-label={`Ampliar foto ${index + 2}`}
                className="group overflow-hidden rounded-2xl border border-stone-200 shadow-sm hover:shadow-lg transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {open !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Foto ampliada ${open + 1} de ${PHOTOS.length}`}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            data-testid="lightbox-close"
            aria-label="Fechar foto ampliada"
            className="absolute top-4 right-4 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
            data-testid="lightbox-prev"
            aria-label="Foto anterior"
            className="absolute left-3 sm:left-6 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <figure
            className="max-w-4xl w-full"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={PHOTOS[open].src}
              alt={PHOTOS[open].alt}
              data-testid="lightbox-image"
              className="max-h-[80vh] w-full rounded-2xl object-contain"
            />
            <figcaption
              data-testid="lightbox-caption"
              className="mt-3 text-center text-sm text-white/80"
            >
              {open + 1} de {PHOTOS.length}
            </figcaption>
          </figure>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
            data-testid="lightbox-next"
            aria-label="Próxima foto"
            className="absolute right-3 sm:right-6 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};
